import { List } from 'immutable'
import { intersection } from 'lodash-es'
import { useState } from 'react'

import { contactAndMapsPlugins } from './MultiColumnBlock'
import { getPluginTypes } from './MultiColumnBlock.helpers'
import { useTranslate } from '../../../../utils/translate'

export function ActionBar({ index, data, element, onDataChange, onEditButtonClick }) {
  const t = useTranslate('interface', 'components.pluginContextBarComponent')

  const [isDeletion, setIsDeletion] = useState(false)

  // single element, no hover menu
  if (isDeletion) {
    return (
      <div className="dali-grid-column-actionbar">
        <DeleteConfirmButton
          data={data}
          index={index}
          onDataChange={(data) => {
            setIsDeletion(false)
            onDataChange(data)
          }}
          onCancel={() => setIsDeletion(false)}
        />
      </div>
    )
  }

  const columnsCount = data.get('columns').size
  const isStart = index === 0
  const isEnd = index === columnsCount - 1
  const isOnlyItem = columnsCount === 1

  const isLastRemainingContactOrMapsItem =
    intersection(getPluginTypes(data.get('columns')), contactAndMapsPlugins).length === 1 &&
    intersection(getPluginTypes(List([element])), contactAndMapsPlugins).length > 0

  const isTextPlugin = element?.get('type') === 'epages.text'
  const isImagePlugin = element?.get('type') === 'epages.image'
  const isPlaceholder = !element.getIn(['data', 'src'])

  const editButtonLabel = t(isImagePlugin ? 'imageElementEditButton.label' : 'editElementButton.label')
  const imageEditButtonLabel = t('editImageFormatButton.label')

  const isSingleEditButton = isOnlyItem && !isTextPlugin && !isImagePlugin

  return (
    <div className="dali-grid-column-actionbar">
      {!isOnlyItem && (
        <>
          <MoveColumnLeftButton data={data} index={index} onDataChange={onDataChange} disabled={isStart} />
          <MoveColumnRightButton data={data} index={index} onDataChange={onDataChange} disabled={isEnd} />
        </>
      )}

      {isImagePlugin && !isPlaceholder && (
        <>
          <button
            type="button"
            className="dali-grid-column-actionbar-button  dali-grid-column-actionbar-button-crop"
            title={imageEditButtonLabel}
            onClick={() => onEditButtonClick('editImage')}
          >
            <span className="visually-hidden">{imageEditButtonLabel}</span>
          </button>

          <button
            type="button"
            className="dali-grid-column-actionbar-button  dali-grid-column-actionbar-button-edit"
            title={editButtonLabel}
            onClick={() => onEditButtonClick(null)}
          >
            <span className={isSingleEditButton ? '' : 'visually-hidden'}>{editButtonLabel}</span>
          </button>
        </>
      )}

      {!isTextPlugin && !isImagePlugin && (
        <button
          type="button"
          className="dali-grid-column-actionbar-button  dali-grid-column-actionbar-button-edit"
          title={editButtonLabel}
          onClick={() => onEditButtonClick(null)}
        >
          <span className={isSingleEditButton ? '' : 'visually-hidden'}>{editButtonLabel}</span>
        </button>
      )}

      {!isOnlyItem && !isLastRemainingContactOrMapsItem && (
        <button
          className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-delete"
          onClick={() => setIsDeletion(true)}
          title={t('deleteButton.label')}
        >
          <span className="visually-hidden">{t('deleteButton.label')}</span>
        </button>
      )}
    </div>
  )
}

function MoveColumnLeftButton({ data, index, onDataChange, disabled }) {
  const t = useTranslate('interface', 'components.pluginContextBarComponent')

  return (
    <button
      className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-left"
      onClick={() => onDataChange(moveColumn(data, index, 'left'))}
      title={t('moveLeftButton.label')}
      disabled={disabled}
    >
      <span className="visually-hidden">{t('moveLeftButton.label')}</span>
    </button>
  )
}

function MoveColumnRightButton({ data, index, onDataChange, disabled }) {
  const t = useTranslate('interface', 'components.pluginContextBarComponent')

  return (
    <button
      className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-right"
      onClick={() => onDataChange(moveColumn(data, index, 'right'))}
      title={t('moveRightButton.label')}
      disabled={disabled}
    >
      <span className="visually-hidden">{t('moveRightButton.label')}</span>
    </button>
  )
}

function DeleteConfirmButton({ data, index, onDataChange, onCancel }) {
  const t = useTranslate('interface', 'components.pluginContextBarComponent')

  return (
    <>
      <span className="dali-grid-column-actionbar-delete-warning">{t('deleteButton.warningMessage')}</span>
      <button
        className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-delete-confirm"
        onClick={() => onDataChange(data.removeIn(['columns', index]))}
      >
        {t('deleteConfirmButton.label')}
      </button>
      <button
        className="dali-grid-column-actionbar-button dali-grid-column-actionbar-button-delete-cancel"
        onClick={onCancel}
      >
        {t('deleteDismissButton.label')}
      </button>
    </>
  )
}

function moveColumn(data: ImmutableMap, index: number, direction: 'left' | 'right') {
  if (index === 0 && direction === 'left') return data
  if (index === data.get('columns').size - 1 && direction === 'right') return data

  const columns = data.get('columns')
  const newIndex = direction === 'left' ? index - 1 : index + 1

  // make sure settings layers follow their anchor in 100% of cases (see EPUI-3132)
  // floating-ui's autoUpdate's layoutShift detection somehow won't recognize column / block moves,
  // but it reliably reruns on scroll events
  setTimeout(() => {
    document.getElementById('body-wrapper')?.dispatchEvent(new Event('scroll'))
  }, 0)

  return data.setIn(['columns', index], columns.get(newIndex)).setIn(['columns', newIndex], columns.get(index))
}
